import { useRef, useState } from "react";
import { Check, Copy, ExternalLink } from "lucide-react";
import { Button } from "./ui";

type CopyLinkFieldProps = {
  label: string;
  value?: string | null;
  disabled?: boolean;
  disabledMessage?: string;
  onCopied?: () => void;
  showOpen?: boolean;
};

export async function copyText(value: string, fallbackElement?: HTMLInputElement | null) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  if (fallbackElement) {
    fallbackElement.focus();
    fallbackElement.select();
  }
  const successful = document.execCommand?.("copy");
  if (!successful) throw new Error("copy-not-supported");
}

export function CopyLinkField({ label, value, disabled = false, disabledMessage, onCopied, showOpen = false }: CopyLinkFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<"idle" | "copied" | "error">("idle");
  const unavailable = disabled || !value;

  async function handleCopy() {
    if (!value || unavailable) return;
    try {
      await copyText(value, inputRef.current);
      setStatus("copied");
      onCopied?.();
      window.setTimeout(() => setStatus("idle"), 2200);
    } catch {
      setStatus("error");
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }

  return (
    <div className="mt-[15px]">
      <div className="mb-1.5 flex items-center justify-between [&_label]:text-[13px] [&_label]:font-extrabold">
        <label htmlFor={`copy-${label.replace(/\W/g, "-")}`}>{label}</label>
        {status === "copied" && <span className="flex items-center gap-[3px] text-xs text-success" role="status"><Check size={14} aria-hidden="true" /> Copiado</span>}
      </div>
      <div className="flex items-center gap-[5px]">
        <input className="h-[42px] min-w-0 flex-1 rounded-[7px] border border-line bg-cream px-2.5 text-[13px] text-muted" id={`copy-${label.replace(/\W/g, "-")}`} ref={inputRef} value={value || "Link disponível após a publicação"} readOnly aria-describedby={unavailable ? `copy-help-${label.replace(/\W/g, "-")}` : undefined} />
        {showOpen && value && !unavailable && <a className="grid size-[42px] shrink-0 place-items-center rounded-[9px] border border-line bg-surface-raised text-ink" href={value} target="_blank" rel="noreferrer" aria-label={`Abrir ${label}`}><ExternalLink size={17} /></a>}
        <Button type="button" onClick={handleCopy} disabled={unavailable} variant="dark" size="sm"><Copy size={15} aria-hidden="true" /> Copiar</Button>
      </div>
      {unavailable && disabledMessage && <small className="mt-[5px] block text-xs text-muted" id={`copy-help-${label.replace(/\W/g, "-")}`}>{disabledMessage}</small>}
      {status === "error" && <small className="mt-[5px] block text-xs text-danger" role="alert">Selecione o link e copie manualmente.</small>}
    </div>
  );
}
